import React from 'react';
import { Home, Trophy, Users } from 'lucide-react';
import { motion } from 'motion/react';

interface BottomNavigationProps {
  currentScreen: 'home' | 'games' | 'teams';
  onNavigateToHome: () => void;
  onNavigateToGames: () => void;
  onNavigateToTeams: () => void;
}

export function BottomNavigation({
  currentScreen,
  onNavigateToHome,
  onNavigateToGames,
  onNavigateToTeams,
}: BottomNavigationProps) {
  const navItems = [
    {
      key: 'home' as const,
      label: 'Home',
      icon: Home,
      onClick: onNavigateToHome,
    },
    {
      key: 'games' as const,
      label: 'Games',
      icon: Trophy,
      onClick: onNavigateToGames,
    },
    {
      key: 'teams' as const,
      label: 'Teams',
      icon: Users,
      onClick: onNavigateToTeams,
    },
  ];

  return (
    <motion.div
      className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50"
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      <div className="max-w-[800px] mx-auto flex">
        {navItems.map((item) => {
          const isActive = currentScreen === item.key;
          const Icon = item.icon;
          
          return (
            <motion.button
              key={item.key}
              onClick={item.onClick}
              className={`flex-1 flex flex-col items-center justify-center py-2 px-4 relative ${
                isActive 
                  ? 'text-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              {/* Active indicator */}
              {isActive && (
                <motion.div
                  className="absolute top-0 left-1/2 w-8 h-0.5 bg-blue-600 rounded-full"
                  layoutId="activeTab"
                  initial={false}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  style={{ x: '-50%' }}
                />
              )}
              
              <motion.div
                className="flex flex-col items-center gap-1"
                animate={{ 
                  scale: isActive ? 1.05 : 1,
                }}
                transition={{ type: "spring", stiffness: 400, damping: 20 }}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                <span className={`text-xs font-medium ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                  {item.label}
                </span>
              </motion.div>
            </motion.button>
          );
        })}
      </div>
      
      {/* Safe area for devices with home indicator */}
      <div className="h-safe-area-inset-bottom bg-white" />
    </motion.div>
  );
}